import React, { useState } from 'react';
import { FaRupeeSign, FaChartLine, FaCalendarAlt, FaInfo, FaShieldAlt, FaQuestion, FaChartPie, FaCalculator, FaBuilding, FaWallet, FaPiggyBank, FaMoneyBillWave, FaBank, FaClock, FaFemale, FaBaby, FaGraduationCap } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';

const SSYCalculator = () => {
  const [formData, setFormData] = useState({
    monthlyDeposit: '',
    years: '',
    maturityAmount: 0,
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateMaturity = () => {
    const { monthlyDeposit, years } = formData;

    if (!monthlyDeposit || !years) {
      setError('Please fill all fields');
      return;
    }

    const rate = 0.076; // Current SSY interest rate (7.6%)
    const months = years * 12;
    const monthlyRate = rate / 12;

    if (monthlyDeposit < 250) {
      setError('Minimum monthly deposit is ₹250');
      return;
    }

    if (years < 1 || years > 21) {
      setError('Investment period must be between 1 and 21 years');
      return;
    }

    // Future Value of Annuity formula
    const maturityAmount = monthlyDeposit * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
    
    setResult({
      maturityAmount: Math.round(maturityAmount),
      totalDeposits: Math.round(monthlyDeposit * months),
      interestEarned: Math.round(maturityAmount - (monthlyDeposit * months)),
    });
    setError('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getPieChartData = () => {
    if (!result) return {};

    const { totalDeposits, interestEarned } = result;

    return {
      options: {
        chart: {
          type: 'donut',
          height: 350,
          toolbar: {
            show: false
          }
        },
        colors: ['#6366F1', '#10B981'],
        labels: ['Principal Amount', 'Interest Earned'],
        legend: {
          position: 'bottom',
          fontSize: '14px',
          fontFamily: 'Inter, sans-serif',
          labels: {
            colors: '#1F2937',
            useSeriesColors: true
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }],
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '14px',
            fontFamily: 'Inter, sans-serif',
            colors: ['#1F2937']
          },
          formatter: function (value) {
            return new Intl.NumberFormat('en-IN').format(value);
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '22px',
                  fontFamily: 'Inter, sans-serif',
                  color: '#1F2937',
                  offsetY: -10
                },
                value: {
                  show: true,
                  fontSize: '26px',
                  fontFamily: 'Inter, sans-serif',
                  color: '#1F2937',
                  offsetY: 16
                },
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '22px',
                  fontFamily: 'Inter, sans-serif',
                  color: '#1F2937'
                }
              }
            }
          }
        }
      },
      series: [totalDeposits, interestEarned]
    };
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-white min-h-screen p-8">
      <Helmet>
        <title>Sukanya Samriddhi Yojana Calculator</title>
        <meta name="description" content="Calculate your Sukanya Samriddhi Yojana returns and maturity amount." />
        <meta property="og:title" content="SSY Calculator" />
        <meta property="og:description" content="Calculate your Sukanya Samriddhi Yojana returns and maturity amount." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <h2 className="text-4xl font-bold text-gray-800 flex items-center">
            <FaFemale className="mr-3 text-4xl text-blue-600" />
            SSY Calculator
          </h2>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">Current Interest Rate:</span>
            <span className="px-4 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              7.6%
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <div className="bg-gradient-to-r from-blue-50 to-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
            <label className="block text-blue-700 text-sm font-bold mb-3 flex items-center">
              <FaWallet className="inline-block mr-2 text-blue-500" />
              Monthly Deposit Amount (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400" />
              <input
                type="number"
                name="monthlyDeposit"
                value={formData.monthlyDeposit}
                onChange={handleInputChange}
                className="w-full pl-12 pr-4 py-3 border border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300"
                placeholder="Enter monthly deposit"
                min="250"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
            <label className="block text-blue-700 text-sm font-bold mb-3 flex items-center">
              <FaCalendarAlt className="inline-block mr-2 text-blue-500" />
              Investment Period (Years)
            </label>
            <select
              value={formData.years}
              onChange={handleInputChange}
              name="years"
              className="w-full p-3 border border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300"
            >
              <option value="1">1 Year</option>
              <option value="2">2 Years</option>
              <option value="3">3 Years</option>
              <option value="4">4 Years</option>
              <option value="5">5 Years</option>
              <option value="6">6 Years</option>
              <option value="7">7 Years</option>
              <option value="8">8 Years</option>
              <option value="9">9 Years</option>
              <option value="10">10 Years</option>
              <option value="11">11 Years</option>
              <option value="12">12 Years</option>
              <option value="13">13 Years</option>
              <option value="14">14 Years</option>
              <option value="15">15 Years</option>
              <option value="16">16 Years</option>
              <option value="17">17 Years</option>
              <option value="18">18 Years</option>
              <option value="19">19 Years</option>
              <option value="20">20 Years</option>
              <option value="21">21 Years</option>
            </select>
          </div>
        </div>

        <button
          onClick={calculateMaturity}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-green-600 font-bold py-6 px-12 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 mb-10 border-4 border-blue-500 flex items-center justify-center gap-3"
        >
          <FaCalculator className="inline-block text-2xl" />
          <span>Calculate SSY Returns</span>
        </button>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {result && (
          <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartPie className="mr-3 text-blue-600" />
              Investment Breakdown
            </h2>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <Chart options={getPieChartData().options} series={getPieChartData().series} type="donut" height={350} />
            </div>
            <div className="mt-6 p-6 bg-blue-50 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">Breakdown Details</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Maturity Amount</span>
                  <span className="font-semibold text-blue-600">
                    {formatCurrency(result.maturityAmount)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Deposits</span>
                  <span className="font-semibold text-blue-600">
                    {formatCurrency(result.totalDeposits)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Interest Earned</span>
                  <span className="font-semibold text-blue-600">
                    {formatCurrency(result.interestEarned)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfo className="mr-3 text-blue-600" />
            About SSY
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-blue-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-blue-700">Key Features</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Government-backed savings scheme</li>
                <li>Higher interest rate than regular savings</li>
                <li>Tax benefits under Section 80C</li>
                <li>Lock-in period of 21 years</li>
                <li>Minimum monthly deposit: ₹250</li>
              </ul>
            </div>
            <div className="p-6 bg-blue-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-blue-700">Benefits</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Guaranteed returns from government</li>
                <li>Higher interest rate compared to regular savings</li>
                <li>Tax benefits under Section 80C</li>
                <li>Financial security for girl child education and marriage</li>
                <li>Flexible partial withdrawal after 18 years</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaQuestion className="mr-3 text-blue-600" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">What is Sukanya Samriddhi Yojana?</h3>
              <p className="text-gray-600">
                Sukanya Samriddhi Yojana is a government-backed savings scheme specifically designed for the welfare of girl children in India.
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">What is the minimum deposit?</h3>
              <p className="text-gray-600">
                The minimum monthly deposit amount is ₹250.
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">What is the interest rate?</h3>
              <p className="text-gray-600">
                The current interest rate is 7.6% per annum, which is higher than regular savings accounts.
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">Can I withdraw before maturity?</h3>
              <p className="text-gray-600">
                Partial withdrawal is allowed after 18 years for education or marriage expenses.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfo className="mr-3 text-blue-600" />
            How to Use
          </h2>
          <div className="space-y-6">
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">Step 1: Enter Monthly Deposit</h3>
              <p className="text-gray-600">
                Input the monthly amount you wish to invest in SSY (minimum ₹250).
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">Step 2: Select Investment Period</h3>
              <p className="text-gray-600">
                Choose your preferred investment period (1-21 years).
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">Step 3: Calculate Returns</h3>
              <p className="text-gray-600">
                Click the "Calculate SSY Returns" button to see your maturity amount and total returns.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSYCalculator;
